import { useCallback, useRef, useState } from 'react';

/**
 * Architectural summary: Tracks width/height of a mounted canvas host node
 * using ResizeObserver and exposes a callback ref for React mount/unmount
 * cycles. Used by PosShellPage view/edit canvases so bounds stay correct
 * across tab switches and remounts.
 */
export function useCanvasHostBounds() {
  const hostNodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const hostRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    hostNodeRef.current = node;
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }

    const rect = node.getBoundingClientRect();
    setWidth(rect.width);
    setHeight(rect.height);

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect;
      setWidth(next?.width ?? 0);
      setHeight(next?.height ?? 0);
    });

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return {
    hostRef,
    width,
    height,
  };
}
